import { useState, useCallback, useEffect } from "react";
import {
  CreditNoteCountsEntity,
  CreditNoteDocumentType,
  GetCreditNoteCountsUseCase,
} from "../../domain";
import { CreditNoteRepositoryImpl } from "../../data/repositories/CreditNoteRepositoryImpl";

// Create repository instance (singleton)
const creditNoteRepository = new CreditNoteRepositoryImpl();

// Create use case
const getCreditNoteCountsUseCase = new GetCreditNoteCountsUseCase(
  creditNoteRepository
);

export interface UseCreditNoteCountsReturn {
  counts: CreditNoteCountsEntity | null;
  loading: boolean;
  error: string | null;
  fetchCounts: (params?: {
    date_from?: string;
    date_to?: string;
    document_type?: CreditNoteDocumentType;
  }) => Promise<void>;
  clearError: () => void;
}

export const useCreditNoteCounts = (initialParams?: {
  date_from?: string;
  date_to?: string;
  document_type?: CreditNoteDocumentType;
}): UseCreditNoteCountsReturn => {
  const [counts, setCounts] = useState<CreditNoteCountsEntity | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dateFrom = initialParams?.date_from;
  const dateTo = initialParams?.date_to;
  const documentType = initialParams?.document_type;

  /**
   * Fetch credit note counts
   */
  const fetchCounts = useCallback(async (params?: {
    date_from?: string;
    date_to?: string;
    document_type?: CreditNoteDocumentType;
  }): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const result = await getCreditNoteCountsUseCase.execute(params);
      setCounts(result);
    } catch (error: any) {
      const errorMessage = error.message || "Failed to load credit note counts";
      setError(errorMessage);
      console.error("Failed to fetch credit note counts:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  // Load initial data on mount
  useEffect(() => {
    fetchCounts({
      date_from: dateFrom,
      date_to: dateTo,
      document_type: documentType,
    });
  }, [fetchCounts, dateFrom, dateTo, documentType]);

  return {
    counts,
    loading,
    error,
    fetchCounts,
    clearError,
  };
};
